/**
 * The top-right account avatar and its menu (SASBND-0009 U2), mirroring dink-journix's
 * `AvatarDropdown`: an initials avatar that opens a `Menu` of account destinations, with
 * **Settings** above **Sign out**.
 *
 * It carries the identity block the shipped account menu already showed (name, email, tenant,
 * role badge) — the shell renders for a principal the brand API would reject, so the menu is
 * where an operator or non-owner reads *which* account they are in before signing out.
 *
 * Built from `Menu`/`MenuItem` rather than `ListItemButton`: SASBND-0009 U1's
 * `MuiListItemButton` override applies the drawer's idle nav coloring to every
 * `ListItemButton` in the app, and `MenuItem` composes `MuiMenuItem-*` only, so it is
 * untouched by it. See the delivery's Architect notes.
 */
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { Principal } from '@/auth/principal';
import { useCopy } from '@/hooks/useCopy';

interface AvatarDropdownProps {
  principal: Principal | null;
  onSignOut: () => void;
}

/**
 * The account-menu badge. Reads the role-derived flags rather than `kind`, so a SAAS_ADMIN
 * operator and a tenant ADMIN/USER are labelled for what they are instead of being shown as
 * admitted principals.
 */
function principalBadge(principal: Principal): { key: string; fallback: string } {
  if (principal.isOwner) return { key: 'principal.owner', fallback: 'Owner' };
  if (principal.isSuperAdmin) {
    return { key: 'principal.superAdmin', fallback: 'Platform super admin' };
  }
  if (principal.kind === 'superadmin') {
    return { key: 'principal.platformAdmin', fallback: 'Platform admin' };
  }
  return { key: 'principal.tenantMember', fallback: 'Tenant member' };
}

/**
 * First + last initial of the display name, falling back to the email's first letter. There
 * is no avatar image in this app: `/api/auth/me` carries no photo, and inventing a lookup for
 * one would be an auth call the contract does not have.
 */
function principalInitials(principal: Principal | null): string {
  const words = (principal?.name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length > 0) {
    const first = words[0] ?? '';
    const last = words.length > 1 ? (words[words.length - 1] ?? '') : '';
    return (first.charAt(0) + last.charAt(0)).toUpperCase();
  }
  return (principal?.email ?? '').trim().charAt(0).toUpperCase() || '?';
}

export function AvatarDropdown({ principal, onSignOut }: AvatarDropdownProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { t } = useCopy();

  // The avatar's accessible name stays "Account" rather than the initials, which are a
  // decoration a screen reader cannot make sense of.
  const label = t('common.account', 'Account');
  const badge = principal ? principalBadge(principal) : null;

  function close() {
    setAnchor(null);
  }

  return (
    <>
      <Tooltip title={principal?.email ?? label}>
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          sx={{ ml: 1, p: 0.5 }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.8125rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {principalInitials(principal)}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {principal && badge ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2">{principal.name ?? principal.email}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {principal.email}
            </Typography>
            {principal.tenant ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {principal.tenant.name}
              </Typography>
            ) : null}
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={t(badge.key, badge.fallback)}
                size="small"
                color={principal.isOwner || principal.isSuperAdmin ? 'primary' : 'default'}
              />
            </Box>
          </Box>
        ) : null}
        <Divider />
        {/* A real link, not a `navigate()` handler: the destination is a route, so it should
            middle-click and open in a new tab like every other navigation in the shell. */}
        <MenuItem component={RouterLink} to="/settings" onClick={close}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('nav.settings', 'Settings')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            close();
            onSignOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.signOut', 'Sign out')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
